/**
 * Apollo mutation options for the Devices page, which has two `fetchMore`-accumulated
 * ("drained") `rescueDevices` lists live at once: IN_FOSTER behind the Fosters section and
 * PENDING_ADOPTIONS behind the Pending / Stalled sections.
 *
 * Every mutation on the page runs `refetchQueries: 'active'` so the summary cards and the
 * server-paginated sections refresh. That is wrong for a drain: a refetch re-runs the query
 * with its *watched* variables — page one — collapsing every page the drain has gathered, so
 * the section blanks to skeletons and re-drains from scratch. Each mutation therefore names
 * the drains it must not collapse.
 *
 * Kept out of Devices.tsx so the sparing rules can be asserted directly (see
 * test/rescue/drainedQueryGuard.test.ts) rather than inferred from the page.
 */

import type { ApolloCache, MutationHookOptions, ObservableQuery, Reference, StoreObject } from '@apollo/client';
import type { RescueDeviceFilter } from '../../lib/rescueQueries';
import { isDrainedDevicesQuery } from '../../hooks/useAllRescueDevices';

/** The `input` shape `skipDrains` reads off a live query; only the identifying fields matter. */
type DevicesQueryInput = { filter?: string; offset?: number } | undefined;

/** Mutation options whose `update` reads the pet id out of the mutation's own variables. */
type PetIdVariables = { variables?: { input?: { petId?: string } } };

/**
 * Removes a pet from one drained `rescueDevices` list by pet id, decrementing its
 * totalCount. Used instead of a refetch wherever a mutation takes a pet *out* of a
 * drained list, for the collapse reason above.
 *
 * Both drained lists on this page need it. PENDING_ADOPTIONS loses a row when a transfer
 * is resolved; IN_FOSTER loses one when a placement is recalled — including the recall
 * inside the foster→adopt flow.
 */
export function removeDrainedDevice(
  cache: ApolloCache<unknown>,
  petGraphqlId: string,
  filter: 'PENDING_ADOPTIONS' | 'IN_FOSTER',
): void {
  cache.modify({
    fields: {
      rescueDevices(existing, { storeFieldName, readField }) {
        // rescueDevices is keyed by its `input` arg, which is serialised into the store field
        // name. Match the drain and nothing else: IN_FOSTER also has a one-row count query
        // live beside it, and emptying *its* items would momentarily read as "the rescue has
        // no foster devices" to anything counting rows. Same discriminator as
        // `isDrainedDevicesQuery` — a drain sends no `offset`, a paginated query always does.
        const isDrain = storeFieldName.includes(filter) && !storeFieldName.includes('"offset"');
        if (!existing || !isDrain) {
          return existing;
        }
        const current = existing as { items: readonly (StoreObject | Reference)[]; totalCount: number };
        const nextItems = current.items.filter((item) => {
          const pet = readField('pet', item) as Reference | StoreObject | undefined;
          return readField('id', pet) !== petGraphqlId;
        });
        const removed = current.items.length - nextItems.length;
        if (removed === 0) {
          return existing;
        }
        return { ...current, items: nextItems, totalCount: Math.max(0, current.totalCount - removed) };
      },
    },
  });
}

/**
 * Builds the `onQueryUpdated` for a mutation that must not collapse a drained list. Names the
 * drained filters to spare; every other active query — the paginated sections, the Fosters
 * badge count, the summary cards — refetches as usual.
 *
 * Returning `undefined` — NOT `false` — skips the refetch WITHOUT suppressing the cache
 * broadcast: Apollo calls onQueryUpdated for a query precisely because a `cache.modify`
 * dirtied it, and `false` would silence that, leaving the just-changed row on screen until
 * an unrelated re-render.
 *
 * Sparing a drain is only safe when the mutation either leaves that list alone or has already
 * edited it in `update`. A list the mutation *adds* to must refetch — there is nothing to
 * insert surgically — which is why the option objects below spare different sets.
 */
export function skipDrains(...spare: RescueDeviceFilter[]) {
  return function onQueryUpdated(observableQuery: ObservableQuery<unknown>) {
    const input = observableQuery.options.variables?.input as DevicesQueryInput;
    if (spare.some((filter) => isDrainedDevicesQuery(input, filter))) {
      return undefined;
    }
    return true;
  };
}

/**
 * The plain options: refetch every active query, drains included. Used by transferring a pet
 * to a foster, the one mutation here that inserts into a drained list —
 * `rescueCreatePetFosterTransfer` does not return the device fields an IN_FOSTER row needs
 * (battery, online state, setup flags), so there is nothing to hand `cache.modify` and a
 * refetch is the only way to surface the new row.
 *
 * The pending drain is refetched too, which a foster placement does not strictly need — it
 * creates no adoption. Left alone rather than shaved: the Pending and Stalled sections are on
 * a different tab from this action, so the collapse is off-screen and self-heals.
 */
export const refetchAllOpts: MutationHookOptions = { refetchQueries: 'active' };

/**
 * Mutations that change neither drained list, so both are spared: unassigning a device from a
 * pet, and the two foster-roster writes.
 *
 * - Unassigning is only reachable from a rescue-held row (the Recall action routes foster rows
 *   to the recall mutation instead), and a rescue-held pet is by definition in neither list:
 *   IN_RESCUE is `NOT in-foster`, and a pending-adoption pet sits in the adopter's household.
 * - Creating a foster home only writes the roster — a home holds no pet until a placement.
 * - Archiving is rejected by the backend while a pet is still placed, so it cannot move a row
 *   out of the foster list either.
 *
 * Without this, archiving a home from the Fosters tab would blank the table it was archived
 * from and re-drain it, for a change that touched none of its rows.
 */
export const drainNeutralOpts: MutationHookOptions = {
  refetchQueries: 'active',
  onQueryUpdated: skipDrains('IN_FOSTER', 'PENDING_ADOPTIONS'),
};

/**
 * Mutation options for resolving a pending adoption transfer (recover / not-recovered /
 * cancel). The resolved transfer is removed from the PENDING_ADOPTIONS drain in `update`
 * instead of refetching it. IN_FOSTER is spared too: creating the adoption already closed any
 * foster placement server-side, and resolving it returns the pet to the rescue rather than to
 * a foster, so that list neither gains nor loses a row.
 *
 * The `rescueUnassignedDeviceInventory` drain is the other accumulated query on this page, and
 * Mark Recovered adds a device to it (the collar returns to unassigned stock). It is
 * intentionally NOT shielded: unlike the pending drain — where resolving is a removal we can
 * apply surgically by pet id — this is an insertion, and the recovered mutation returns only
 * `transfer { id status }`, not the module fields (battery/online/lastConnection) an inventory
 * row needs. So we let it refetch to surface the recovered device. That collapses the drain
 * and re-drains, but it self-heals and only churns for rescues holding more than one page of
 * unassigned devices — the same case where a single refetch couldn't avoid the collapse anyway
 * (the server clamps page size).
 */
export const pendingResolveOpts: MutationHookOptions = {
  refetchQueries: 'active',
  onQueryUpdated: skipDrains('PENDING_ADOPTIONS', 'IN_FOSTER'),
  update(cache: ApolloCache<unknown>, _result: unknown, options: PetIdVariables) {
    const petId = options.variables?.input?.petId;
    if (petId) {
      removeDrainedDevice(cache, petId, 'PENDING_ADOPTIONS');
    }
  },
};

/**
 * Mutation options for recalling a foster placement — the Recall action, and the recall that
 * opens the foster→adopt flow. The recalled pet is removed from the IN_FOSTER drain in
 * `update`, which is exact: a recall is a removal, and the pet id identifies the row. Since
 * the section only renders once the drain is `complete`, a refetch-collapse here would blank
 * the table to skeletons on the primary action of the tab.
 *
 * PENDING_ADOPTIONS is spared as well because a recall does not touch it at all — the pet
 * returns to the rescue, no adoption is created — so refetching it would collapse that drain
 * for nothing, dropping rows out of the Pending and Stalled sections until it re-ran.
 */
export const fosterRecallOpts: MutationHookOptions = {
  refetchQueries: 'active',
  onQueryUpdated: skipDrains('IN_FOSTER', 'PENDING_ADOPTIONS'),
  update(cache: ApolloCache<unknown>, _result: unknown, options: PetIdVariables) {
    const petId = options.variables?.input?.petId;
    if (petId) {
      removeDrainedDevice(cache, petId, 'IN_FOSTER');
    }
  },
};

/**
 * Adopting out spares the IN_FOSTER drain but not the pending one.
 *
 * From the Fosters tab the flow recalls first — which already removed the row from the foster
 * drain in `update` — and from Activated the foster list is not involved at all, so refetching
 * it can only collapse pages it did not need to change. PENDING_ADOPTIONS is the opposite: the
 * adoption it creates is a row that has to appear there, and an insertion is not something we
 * can apply surgically, so that drain does refetch.
 */
export const adoptOutOpts: MutationHookOptions = {
  refetchQueries: 'active',
  onQueryUpdated: skipDrains('IN_FOSTER'),
};
